import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import ContactConnectCell from "../../CustomizedTable/CustomCells/ContactConnectCell/ContactConnectCell";
import ContactConnectCapstoneCell from "../../CustomizedTable/CustomCells/ContactConnectCapstoneCell/ContactConnectCapstoneCell";
import InlineIconButton from "../../Buttons/InlineIconButton/InlineIconButton";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { sortArrayOfStringObjectsAsc } from "../../../utils/sortHelpers";
import { SecurityService } from "../../../services/SecurityService";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadProfileContacts({
  activeLoad,
  handleTableAction
}) {
  let windowWidth = window.innerWidth - 85;
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [contactsTransportation, setContactsTransportation] = useState([]);
  const [contactsCapstone, setContactsCapstone] = useState([]);
  const [movement, setMovement] = useState({});
  const [carrier, setCarrier] = useState({});
  const [dispatchers, setDispatchers] = useState([]);
  const [tableWidth, setTableWidth] = useState(windowWidth / 2);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell
      },
      {
        Header: "Role",
        accessor: "role",
        filter: "fuzzyText",
        width: 160,
        Cell: DefaultCell
      },
      {
        Header: "Actions",
        width: 100,
        Cell: ContactConnectCell
      },
    ]
  );

  const columnsCapstoneContacts = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell
      },
      {
        Header: "Role",
        accessor: "role",
        filter: "fuzzyText",
        width: 160,
        Cell: DefaultCell
      },
      {
        Header: "Actions",
        width: 100,
        Cell: ContactConnectCapstoneCell
      },
    ]
  );

  useEffect(() => {
    if (movement) {
      setContactsTransportation([
        {
          name: movement.driverName,
          role: "Driver",
          phone: "",
          mobile: movement.driverCell,
          email: movement.driverEmail
        },
        {
          name: movement.carrierContact,
          role: "Primary carrier contact",
          phone: movement.carrierPhone,
          mobile: "",
          email: movement.carrierEmail
        },
        {
          name: movement.afterHours,
          role: "After hours",
          phone: "",
          mobile: "",
          email: ""
        }
      ]);

      let carrierRep = {};
      if (dispatchers.length) {
        carrierRep = dispatchers.find(({ userIdMcld }) => userIdMcld === movement.dispatcherId);
      }

      setContactsCapstone([
        {
          name: carrierRep ? carrierRep.userName : movement.dispatcherId,
          role: "Carrier rep",
          phone: "",
          mobile: "",
          email: carrierRep?.email
        },
        {
          name: activeLoad.salesRep,
          role: "Sales rep",
          phone: "",
          mobile: "",
          email: ""
        },
        {
          name: activeLoad.accountManager,
          role: "Account manager",
          phone: "",
          mobile: "",
          email: ""
        },
        {
          name: activeLoad.operationsRep,
          role: "Ops rep",
          phone: "",
          mobile: "",
          email: ""
        },
      ]);
    }

  }, [movement, dispatchers]);

  useEffect(() => {
    if (activeLoad.movements) {
      setMovement(activeLoad.movements[0]);
    }

    if (activeLoad.payeeId) {
      CarrierService.GetCarrier(instance, activeLoad.payeeId).then(res => {
        setCarrier(res);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [activeLoad]);

  useEffect(() => {
    SecurityService.GetUsers(instance).then(res => {
      let sortedUsers = sortArrayOfStringObjectsAsc(res, "userIdMcld");
      sortedUsers.forEach(u => { u.name = u.userName; });
      setDispatchers(sortedUsers);
    }).catch(error => {
      console.error(error);
      warning(error);
    });

    const handleWindowResize = () => setTimeout(() => {
      windowWidth = window.innerWidth - 85;
      setTableWidth(windowWidth / 2);
    }, 300);

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return (
    <Box className="Load-profile-component" sx={{ overflowY: "hidden" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: 1, marginX: "20px" }}>
            <Box sx={{ display: "flex", paddingBottom: "10px" }} >
              {carrier?.id ? (
                <>
                  <Typography
                    variant="h3"
                    marginRight="10px"
                    color="primary.60"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleTableAction({ action: "carrier-profile", carrierId: carrier.id })}>
                    {carrier.name}
                  </Typography>
                  <InlineIconButton
                    onClick={() => handleTableAction({ action: "load-contact", loadId: activeLoad.id, carrierId: activeLoad.payeeId })}
                    icon={EditOutlinedIcon}
                    buttonColor="blue"
                  />
                </>
              ) : (
                <Typography variant="h3" marginRight="10px" color="black_opacity.80">
                  No carrier assigned yet
                </Typography>
              )}
            </Box>
            {movement.carrierTractor || movement.carrierTrailer ?
              <Typography variant="subhead2">
                Tractor {movement.carrierTractor} / Trailer {movement.carrierTrailer}
              </Typography> : <></>
            }
            <CustomizedTable
              id="load-profile-contacts"
              columns={columns}
              data={contactsTransportation}
              defaultHeight={contactsTransportation.length * 32}
              defaultWidth={tableWidth}
            />
          </Box>
          <Box sx={{ flexGrow: 1, marginX: "20px" }}>
            <Box sx={{ display: "flex", paddingBottom: "10px", paddingRight: "20px" }} >
              <Typography variant="h3" marginRight="10px">
                LoadDelivered
              </Typography>
            </Box>
            <CustomizedTable
              id="load-profile-contacts"
              columns={columnsCapstoneContacts}
              data={contactsCapstone}
              defaultHeight={contactsCapstone.length * 32}
              defaultWidth={tableWidth}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

LoadProfileContacts.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  handleTableAction: PropTypes.func
};